import { useTranslation } from "react-i18next";
import ListPage from "../../../layouts/page/ListPage";
import "./CustomerList.css";
import { Columns } from "./Columns";
import MenuConfig from "../../../../constants/MenuConfig";
import { Alert, Badge, Dropdown, Col, FormLabel, Row, Spinner, Table } from "react-bootstrap";
import { useState } from "react";
import AddressIcon from "../../../components/Icon/AddressIcon";
import ClubCrownIcon from "../../../components/Icon/ClubCrownIcon";
import ClubLateIcon from "../../../components/Icon/ClubLateIcon";
import ModalPage from "../../../components/Common/Modal/ModalPage";
import { warnAlert } from "../../../components/alert/defaultAlert";
import AddressList from "../../../components/Custom/Common/Address/AddressList";
import Api from "../../../../constants/Api";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import InputText from "../../../components/Forms/Element/InputText";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import StatusFilterSelect, { DEFAULT_STATUS, buildStatusFilter } from "../../../components/Common/Filter/StatusFilterSelect";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import { SearchOperation } from "../../../../utils/ApiUtils";
import axiosInstance from "../../../../services/AxiosInstance";
import { formatDateTime } from "../../../../utils/DateUtils";

const appointmentStatus = {
    SCHEDULED: { label: 'Agendado', color: 'secondary' },
    CONFIRMED: { label: 'Confirmado', color: 'success' },
    IN_PROGRESS: { label: 'Em andamento', color: 'primary' },
    COMPLETED: { label: 'Concluído', color: 'dark' },
    CANCELED: { label: 'Cancelado', color: 'warning' },
    NO_SHOW: { label: 'Não compareceu', color: 'danger' }
};

const CustomerList = () => {

    const { t } = useTranslation();
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [parentId, setParentId] = useState(null);
    const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
    const [customerAppointments, setCustomerAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [appointmentsError, setAppointmentsError] = useState('');
    const [appointmentsCustomerName, setAppointmentsCustomerName] = useState('');
    
    const [nameFilter, setNameFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [documentFilter, setDocumentFilter] = useState('');
    const [clubFilter, setClubFilter] = useState(null);
    const [branchFilter, setBranchFilter] = useState(null);
    // A grade já abre só com ativos, então o select nasce em Ativo: o campo mostra o recorte
    // que está valendo em vez de parecer vazio.
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
    const [filters, setFilters] = useState([]);

    // Filtro sintético traduzido no CustomerService: assinante é quem possui assinatura
    // ativa e não cancelada, a mesma regra do selo exibido na grade.
    const clubOptions = [
        { value: true, label: t('yes') },
        { value: false, label: t('no') }
    ];

    const onShowModalAddress = () => {
        if(selectedRows !== null && selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }

        setParentId(selectedRows[0].id);
        setShowAddressModal(true);
    }

    const onShowLatestAppointments = () => {
        if (!selectedRows || selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }

        const customer = selectedRows[0];
        setAppointmentsCustomerName(customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(' '));
        setCustomerAppointments([]);
        setAppointmentsError('');
        setAppointmentsLoading(true);
        setShowAppointmentsModal(true);

        axiosInstance.get(`${Api.appointment.byCustomer}/${customer.id}/latest`)
            .catch((error) => {
                // Compatibilidade durante a publicação: servidores ainda sem o endpoint
                // resumido podem responder 404; o endpoint anterior já vem em ordem decrescente.
                if (error?.response?.status === 404) {
                    return axiosInstance.get(`${Api.appointment.byCustomer}/${customer.id}`);
                }
                throw error;
            })
            .then((response) => setCustomerAppointments(Array.isArray(response.data) ? response.data.slice(0, 10) : []))
            .catch((error) => {
                const serverMessage = error?.response?.data?.message;
                setAppointmentsError(serverMessage || 'Não foi possível carregar o histórico de agendamentos. Tente novamente.');
            })
            .finally(() => setAppointmentsLoading(false));
    };

    const getServices = (appointment) => {
        if (Array.isArray(appointment.serviceNames) && appointment.serviceNames.length > 0) {
            return appointment.serviceNames.join(', ');
        }
        return appointment.service?.name || '-';
    };

    const handleSearch = () => {
        const activeFilters = [];

        if (nameFilter && nameFilter.trim()) {
            activeFilters.push({
                field: "fullName",
                value: nameFilter.trim(),
                // Busca por palavra: o cadastro costuma ter nome do meio, e digitar apenas
                // o primeiro nome e o sobrenome não encontrava o cliente.
                operation: SearchOperation.MATCH_WORDS
            });
        }
        
        if (phoneFilter && phoneFilter.trim()) {
            activeFilters.push({
                field: "phone",
                value: phoneFilter.trim(),
                operation: SearchOperation.MATCH
            });
        }
        
        if (documentFilter && documentFilter.trim()) {
            activeFilters.push({
                field: "document",
                value: documentFilter.trim(),
                operation: SearchOperation.MATCH
            });
        }

        // Sem seleção o filtro não entra, mantendo assinantes e não assinantes na lista.
        if (clubFilter) {
            activeFilters.push({
                field: "clubMember",
                value: clubFilter.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (branchFilter) {
            activeFilters.push({
                field: "branch.id",
                value: branchFilter.value,
                operation: SearchOperation.EQUAL
            });
        }

        activeFilters.push(buildStatusFilter(statusFilter));

        setFilters(activeFilters);
    };

    const handleClearFilters = () => {
        setNameFilter('');
        setPhoneFilter('');
        setDocumentFilter('');
        setClubFilter(null);
        setBranchFilter(null);
        setStatusFilter(DEFAULT_STATUS);
        setFilters([]);
    };

    // Legenda dos selos do clube, entregue ao ListPage como slot para ficar entre a barra
    // de ações e a grade: ali ela encosta na coluna que explica. Acima dos botões, parecia
    // parte do filtro e ficava longe das linhas.
    //
    // A coroa aparece uma vez só, no item que ela define; no outro o que muda é o ícone ao
    // lado, e é ele que precisa ser reconhecido.
    const clubLegend = (
        <div className="customer-legend">
            <span className="customer-legend__title">{t('caption')}</span>
            <span className="customer-legend__item">
                <span className="customer-legend__icon">
                    <ClubCrownIcon className="text-warning" title="" />
                </span>
                Membro do Clube
            </span>
            <span className="customer-legend__item">
                <span className="customer-legend__icon">
                    <ClubLateIcon className="text-danger" title="" />
                </span>
                Mensalidade em atraso, mantém o benefício
            </span>
        </div>
    );

    return (
        <>
            <ModalPage 
                title={t('address_registered')}
                show={showAddressModal} 
                setShow={setShowAddressModal} 
                child={<AddressList parentId={parentId}/>}
                size="lg"
            />

            <ModalPage
                title={appointmentsCustomerName ? `Últimos agendamentos de ${appointmentsCustomerName}` : 'Últimos agendamentos'}
                show={showAppointmentsModal}
                setShow={setShowAppointmentsModal}
                size="lg"
                child={
                    appointmentsLoading ? (
                        <div className="d-flex justify-content-center align-items-center gap-2 py-5">
                            <Spinner animation="border" size="sm" />
                            <span>Carregando agendamentos...</span>
                        </div>
                    ) : appointmentsError ? (
                        <Alert variant="danger" className="mb-0">{appointmentsError}</Alert>
                    ) : customerAppointments.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="fa-regular fa-calendar-xmark fa-2x mb-3 d-block"></i>
                            <strong className="d-block text-body">Nenhum agendamento encontrado</strong>
                            <span>Este cliente ainda não possui agendamentos.</span>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Data e horário</th>
                                        <th>Profissional</th>
                                        <th>Serviços</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customerAppointments.map((appointment) => {
                                        const status = appointmentStatus[appointment.appointmentStatus] || {
                                            label: appointment.appointmentStatus || '-', color: 'secondary'
                                        };
                                        return (
                                            <tr key={appointment.id}>
                                                <td className="text-nowrap">{formatDateTime(appointment.scheduledDate)}</td>
                                                <td>{appointment.employee?.fullName || '-'}</td>
                                                <td>{getServices(appointment)}</td>
                                                <td><Badge bg={status.color}>{status.label}</Badge></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )
                }
            />
            
            <PageTitle activeMenu={t("customers")} motherMenu={t("main")} />

            <FilterAccordion
                title="filters"
                onSearch={handleSearch}
                onClear={handleClearFilters}
                centerButtons={false}
            >
                <Row className="g-2 align-items-end">
                    <Col lg={3} md={6}>
                        <FormLabel>{t('name')}</FormLabel>
                        <InputText
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            placeholder={t('name')}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('phone')}</FormLabel>
                        <InputText
                            value={phoneFilter}
                            onChange={(e) => setPhoneFilter(e.target.value)}
                            placeholder={t('phone')}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('cpf')}</FormLabel>
                        <InputText
                            value={documentFilter}
                            onChange={(e) => setDocumentFilter(e.target.value)}
                            placeholder={t('cpf')}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('has_subscription')}</FormLabel>
                        <CustomSelect
                            value={clubFilter}
                            onChange={(option) => setClubFilter(option)}
                            options={clubOptions}
                            placeholder={t('all')}
                            isClearable
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>Unidade de cadastro</FormLabel>
                        <BranchSelect
                            value={branchFilter}
                            onChange={(option) => setBranchFilter(option)}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListPage
                showPageTitle={false}
                activeMenu={t("customers")}
                motherMenu={t("main")}
                path={Api.main.customer}
                route={MenuConfig.main.customer.route}
                columns={Columns}
                setSelectedRows={setSelectedRows}
                customButtons={
                    <>
                        <Dropdown.Item onClick={onShowModalAddress}>
                            <AddressIcon /> {t('addresses')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={onShowLatestAppointments}>
                            <i className="fa-regular fa-calendar me-2"></i> Últimos agendamentos
                        </Dropdown.Item>
                    </>
                }
                filters={filters}
                hideInactiveToggle
                beforeGrid={clubLegend}
            />
        </>
    );
}

export default CustomerList;
